import React, {forwardRef, useCallback, useMemo} from 'react'
import PropTypes from 'prop-types'
import useProductReviewOverview from '../../bazaarvoice-api/hooks/use-product-review-overview'
import ReviewOverview from '../review-overview'
import ReviewOverviewSkeleton from '../review-overview/skeleton'
import {convertBVRatingValue} from './utils'
import useWriteProductReviewModal from '../../bazaarvoice-api/hooks/use-write-product-review-modal'
import {trackRRClickForReview} from '../../tracking/analytics'
import {getBVReviewProductIds} from '../../utils/product-utils'

const BVProductReviewOverview = forwardRef(
    ({product, onCloseReviewModal, isOpenReviewModal, onOpenReviewModal}, ref) => {
        const pid = useMemo(() => getBVReviewProductIds(product), [product])
        const {data, error, loading} = useProductReviewOverview(pid)
        const {loading: writeReviewLoading, onOpen: onOpenWriteReview} =
            useWriteProductReviewModal()

        const [totalRating, ratingsBreakdown] = useMemo(
            () => [
                // total rating
                convertBVRatingValue(
                    data?.statistics?.AverageOverallRating,
                    data?.statistics?.OverallRatingRange
                ),
                // ratings breakdown
                data?.statistics?.RatingDistribution?.map((ratingDistribution) => ({
                    rating: convertBVRatingValue(
                        ratingDistribution.RatingValue,
                        data.statistics.OverallRatingRange
                    ),
                    count: ratingDistribution.Count
                })).sort((a, b) => b.rating - a.rating)
            ],
            [data]
        )

        const onWriteReview = useCallback(() => {
            onOpenWriteReview(pid)
            trackRRClickForReview()
        }, [onOpenWriteReview, pid])

        if (loading) {
            return <ReviewOverviewSkeleton ref={ref} />
        }

        if (error) {
            return null
        }

        return (
            <ReviewOverview
                ref={ref}
                ratingsBreakdown={ratingsBreakdown}
                review={data?.review}
                reviewsCount={data?.statistics?.TotalReviewCount || 0}
                totalRating={totalRating || 0}
                writeReviewLoading={writeReviewLoading}
                onWriteReview={onWriteReview}
                productSearchIdList={pid}
                onCloseReviewModal={onCloseReviewModal}
                isOpenReviewModal={isOpenReviewModal}
                onOpenReviewModal={onOpenReviewModal}
            />
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    BVProductReviewOverview.displayName = 'BVProductReviewOverview'
}

BVProductReviewOverview.propTypes = {
    product: PropTypes.object,
    onCloseReviewModal: PropTypes.func,
    isOpenReviewModal: PropTypes.bool,
    onOpenReviewModal: PropTypes.func
}

export default BVProductReviewOverview
